import { call, fork, put, select, take, takeEvery } from "typed-redux-saga";
import { Action } from "@reduxjs/toolkit";
import { getApiErrorMessage } from "../../util/errors";
import { fetchTextEditorFileContentsStarted, foundTextEditorFilePathsSet, saveTextEditorFileContentsFinished, saveTextEditorFileContentsStarted, textEditorFileContentsFetched, textEditorFileContentsSet, textEditorFilesFetched, textEditorSelectors } from "./textEditorSlice";
import { TextEditorFile } from "./text-editor-file";
import TextEditorFilesApi from "../../web-apis/text-editor-files-api";


function* fetchTextEditorFilesSaga() {
    let files: TextEditorFile[] = [];
    let error = null;

    try {
        files = yield* call(TextEditorFilesApi.searchFiles);
    }
    catch (ex) {
        console.error(ex);
        error = ex;
    }

    if (error === null) {
        yield* put(foundTextEditorFilePathsSet(files));
        for (const file of files) {
            // after putting text editor files to store the file objects we have here may already
            // be stale, so immediately re-fetch latest version of this file from the store so we
            // don't have to duplicate store logic here
            //
            // TODO: test that this still works correctly after the RTK refactor!
            //
            const latestFile = yield* select(textEditorSelectors.selectLatestTextEditorFile, file);
            yield* fork(tryFetchTextEditorFileContentsSaga, latestFile);
        }
    }
}

function* tryFetchTextEditorFileContentsSaga(file: TextEditorFile | undefined) {
    if (file !== undefined) {
        // allow file to load (or re-load) if it's not already loading, or doesn't have modifications
        if (!file.isLoadingFile && !file.isModified) {
            yield* put(textEditorFileContentsFetched(file.filePath));

            let contents: string = '';
            let error = null;

            try {
                contents = yield* call(TextEditorFilesApi.loadFile, file.filePath);
            }
            catch (ex) {
                console.error(ex);
                error = ex;
            }

            if (error === null) {
                // TODO: show errors in UI
                yield* put(textEditorFileContentsSet({ filePath: file.filePath, contents }));
            }
        }
    }
}

function* saveTextEditorFileContentsSaga(action: Action) {
    if (saveTextEditorFileContentsStarted.match(action)) {
        const { filePath, newContents } = action.payload;

        let error = null;

        try {
            yield* call(TextEditorFilesApi.saveFile, filePath, newContents);
        }
        catch (ex) {
            console.error(ex);
            error = ex;
        }

        if (error === null) {
            yield* put(saveTextEditorFileContentsFinished({ filePath: filePath, newContents: newContents, error: undefined }));
        } else {
            // error case
            console.error(`Was not able to save changes to file ${filePath}`);
            const errorMessage = getApiErrorMessage(error);
            yield* put(saveTextEditorFileContentsFinished({ filePath: filePath, newContents: null, error: errorMessage }));
        }
    }
}



function* watchFetchTextEditorFilesSaga() {
    yield* takeEvery(textEditorFilesFetched, fetchTextEditorFilesSaga);
}

function* watchFetchTextEditorFileContentsStartedSaga() {
    while (true) {
        const action = yield* take(fetchTextEditorFileContentsStarted);

        yield* fork(tryFetchTextEditorFileContentsSaga, action.payload);
    }
}

function* watchSaveTextEditorFileContentsSaga() {
    yield* takeEvery(saveTextEditorFileContentsStarted, saveTextEditorFileContentsSaga);
}

/** Returns all relevant watches to be added to a main root watch saga */
export function getWatchesForTextEditorSagas() {
    return [
        watchFetchTextEditorFilesSaga(),
        watchFetchTextEditorFileContentsStartedSaga(),
        watchSaveTextEditorFileContentsSaga(),
    ];
}
